/**
 * LinkContentContent
 *
 * Body of the Link Content sheet. Renders a FlexibleHeaderList with a 3-column
 * grid of the current user's linkable medias, mirroring Flutter's
 * LinkContentScreen.
 */

import React, { useCallback } from 'react';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useGridColumnsCount } from '@/presentation/hooks';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { useLinkContent } from '../hooks/useLinkContent';
import { LinkContentMediaCell } from './LinkContentMediaCell';
import { LinkContentStatusView } from './LinkContentStatusView';

interface LinkContentContentProps {
  title: string;
  excludeMediaId: string;
  onResolve: (media: UserMediaEntity | null) => void;
}

export const LinkContentContent: React.FC<LinkContentContentProps> = ({
  title,
  excludeMediaId,
  onResolve,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const gridColumnsCount = useGridColumnsCount();

  const { medias, hasMore, isInitialLoading, isLoadingMore, error, loadMore } =
    useLinkContent({ excludeMediaId });

  const handleClose = useCallback(() => {
    onResolve(null);
    SheetService.close();
  }, [onResolve]);

  const handleItemTap = useCallback(
    (media: UserMediaEntity) => {
      onResolve(media);
      SheetService.close();
    },
    [onResolve],
  );

  const renderMediaCell = useCallback(
    (media: UserMediaEntity) => (
      <LinkContentMediaCell media={media} onTap={handleItemTap} />
    ),
    [handleItemTap],
  );

  const closeAction = (
    <button
      {...qaAttr({ id: 'link-content-close' })}
      onClick={handleClose}
      className="hover:opacity-80 transition-opacity focus:outline-none"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: responsive.spacing.xs,
      }}
      aria-label={l10n.common.close}
    >
      <AssetIcon
        path="/assets/images/close.png"
        width={responsive.icon.small}
        height={responsive.icon.small}
        color={theme.icon.primary}
      />
    </button>
  );

  return (
    <div style={{ backgroundColor: theme.modalBackground, height: '100%' }}>
      <FlexibleHeaderList<UserMediaEntity>
        items={medias}
        renderItem={renderMediaCell}
        getItemKey={(media) => media.id}
        gridProps={{
          columnsCount: gridColumnsCount,
          crossAxisSpacing: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
          mainAxisSpacing: CONTENT_CONSTANTS.gridLayout.mainAxisSpacing,
          itemAspectRatio: CONTENT_CONSTANTS.gridLayout.itemAspectRatio,
          bufferRows: CONTENT_CONSTANTS.gridLayout.bufferRows,
        }}
        onLoadMore={loadMore}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        emptyListComponent={
          <LinkContentStatusView variant={isInitialLoading ? 'loading' : 'empty'} />
        }
        errorOnFirstFetchComponent={error ? <LinkContentStatusView variant="error" /> : undefined}
        backgroundColor={theme.modalBackground}
        appBarOptions={{
          title,
          hideLeading: true,
          actions: closeAction,
          backgroundColor: theme.modalBackground,
        }}
      />
    </div>
  );
};
